import { useEffect, useMemo, useState } from 'react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { Download, TrendingUp, AlertTriangle, Trophy, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const WINDOWS = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
];

export default function ManagerAnalyticsPage() {
  const { api } = useAuth();
  const [windowDays, setWindowDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/manager/analytics?days=${windowDays}`);
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) toast.error('Could not load analytics');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [api, windowDays]);

  const summary = data?.summary || {};
  const previous = data?.previous || {};

  // Compute delta pills (current vs previous period)
  const deltas = {
    completionRate: round1((summary.completionRate ?? 0) - (previous.completionRate ?? 0)),
    totalPosted:    (summary.totalPosted ?? 0)   - (previous.totalPosted ?? 0),
    cancelled:      (summary.cancelled ?? 0)     - (previous.cancelled ?? 0),
    completed:      (summary.completed ?? 0)     - (previous.completed ?? 0),
  };
  const trendData = useMemo(
    () => (data?.trend || []).map((r) => ({ ...r, label: r.date.slice(5) })),
    [data]
  );
  const penaltyData = useMemo(
    () => (data?.penaltyTrend || []).map((r) => ({ ...r, label: r.weekStart.slice(5) })),
    [data]
  );
  const topWorkers = data?.topWorkers || [];

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get('/manager/shifts/export.csv', { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.download = `shiftcover365_shifts_${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Shift history CSV downloaded');
    } catch (e) {
      toast.error('Could not export CSV');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto" data-testid="manager-analytics-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#DA291C] mb-1.5">Analytics</p>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Performance overview</h1>
          <p className="text-[13.5px] text-slate-500 mt-1">Completion rates, penalty trends and top performers across your shifts.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-full bg-slate-100 p-1" data-testid="analytics-window-toggle">
            {WINDOWS.map((w) => (
              <button
                key={w.days}
                onClick={() => setWindowDays(w.days)}
                className={`px-3 h-11 min-w-[44px] text-[12px] font-semibold rounded-full transition-colors ${
                  windowDays === w.days ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
                data-testid={`analytics-window-${w.label}`}
              >
                {w.label}
              </button>
            ))}
          </div>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="inline-flex items-center gap-2 rounded-full bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60 px-4 h-11 text-[12.5px] font-semibold transition-colors"
            data-testid="analytics-export-csv"
          >
            {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            Export CSV
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8" data-testid="analytics-kpis">
        <Kpi label="Completion rate" value={`${summary.completionRate ?? 0}%`} sub={`${summary.completed ?? 0} completed`} accent="emerald" delta={deltas.completionRate} deltaSuffix="pp" higherIsBetter />
        <Kpi label="Shifts posted" value={summary.totalPosted ?? 0} sub={`Last ${windowDays} days`} accent="slate" delta={deltas.totalPosted} higherIsBetter />
        <Kpi label="Cancelled" value={summary.cancelled ?? 0} sub="Shifts cancelled" accent="amber" delta={deltas.cancelled} higherIsBetter={false} />
        <Kpi label="Open / Assigned" value={`${summary.openNow ?? 0} / ${summary.assigned ?? 0}`} sub="Pipeline now" accent="indigo" />
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-4 sm:gap-6 mb-8">
        <ChartCard
          title="Shift completion trend"
          icon={TrendingUp}
          empty={trendData.every((r) => r.posted === 0)}
          testid="chart-completion-trend"
        >
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={trendData} margin={{ top: 10, right: 16, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 11, fill: '#64748b' }} allowDecimals={false} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }} />
              <Line type="monotone" dataKey="posted" stroke="#94a3b8" strokeWidth={2} dot={false} name="Posted" />
              <Line type="monotone" dataKey="completed" stroke="#059669" strokeWidth={2} dot={false} name="Completed" />
              <Line type="monotone" dataKey="cancelled" stroke="#DA291C" strokeWidth={2} dot={false} name="Cancelled" />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Worker penalty trend"
          icon={AlertTriangle}
          empty={penaltyData.length === 0}
          testid="chart-penalty-trend"
        >
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={penaltyData} margin={{ top: 10, right: 16, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} />
              <YAxis tick={{ fontSize: 11, fill: '#64748b' }} allowDecimals={false} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }} />
              <Bar dataKey="lateCancel" stackId="a" fill="#f59e0b" name="Late cancel" radius={[4, 4, 0, 0]} />
              <Bar dataKey="noShow"    stackId="a" fill="#DA291C" name="No-show"     radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Top workers */}
      <div className="rounded-2xl border border-slate-200 bg-white" data-testid="analytics-top-workers-card">
        <div className="flex items-center gap-2 px-5 sm:px-6 py-4 border-b border-slate-100">
          <Trophy className="h-4 w-4 text-amber-500" />
          <h3 className="text-[14.5px] font-bold text-slate-900">Top performing workers</h3>
          <span className="ml-auto text-[11.5px] text-slate-400">By completed shifts × 0.7 + avg rating × 0.3</span>
        </div>
        {loading ? (
          <EmptyState icon={Loader2} text="Loading…" spinning />
        ) : topWorkers.length === 0 ? (
          <EmptyState icon={Trophy} text="No completed shifts yet in this window." />
        ) : (
          <ul className="divide-y divide-slate-100">
            {topWorkers.map((w, i) => (
              <li key={w.id} className="px-5 sm:px-6 py-3.5 flex items-center gap-4" data-testid={`top-worker-${i + 1}`}>
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-[12px] font-bold text-slate-700 shrink-0">
                  {i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[14px] font-semibold text-slate-900 truncate">{w.fullName || 'Unknown worker'}</p>
                  <p className="text-[12px] text-slate-500">
                    {w.completedShiftsCount} completed · {w.ratingAverage.toFixed(1)}★ · reliability {w.reliabilityScore.toFixed(0)}%
                  </p>
                </div>
                <span className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 text-[11.5px] font-semibold px-2.5 h-6 tabular-nums">
                  {w.performanceScore.toFixed(2)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, accent, delta, deltaSuffix = '', higherIsBetter = true }) {
  const accents = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    amber:   'bg-amber-50 text-amber-700 border-amber-100',
    indigo:  'bg-indigo-50 text-indigo-700 border-indigo-100',
    slate:   'bg-slate-100 text-slate-700 border-slate-200',
  };
  const hasDelta = typeof delta === 'number' && Number.isFinite(delta) && delta !== 0;
  const isPositive = delta > 0;
  const isGood = higherIsBetter ? isPositive : !isPositive;
  const deltaCls = isGood
    ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
    : 'bg-red-50 text-red-700 border-red-100';
  const arrow = isPositive ? '↑' : '↓';
  const formatted = `${arrow} ${Math.abs(delta)}${deltaSuffix}`;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">{label}</p>
        {hasDelta && (
          <span
            className={`inline-flex items-center rounded-full border text-[10.5px] font-semibold px-1.5 h-4.5 tabular-nums ${deltaCls}`}
            data-testid={`kpi-delta-${label.toLowerCase().replace(/\s+/g, '-')}`}
            title="Change vs previous period"
          >
            {formatted}
          </span>
        )}
      </div>
      <p className="mt-2 text-2xl sm:text-3xl font-bold tabular-nums text-slate-900 tracking-tight">{value}</p>
      <span className={`mt-2 inline-flex items-center rounded-full border text-[11px] font-medium px-2 h-5 ${accents[accent] || accents.slate}`}>
        {sub}
      </span>
    </div>
  );
}

function round1(n) {
  return Math.round(n * 10) / 10;
}

function ChartCard({ title, icon: Icon, children, empty, testid }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="h-4 w-4 text-slate-500" />
        <h3 className="text-[14px] font-bold text-slate-900">{title}</h3>
      </div>
      {empty ? <EmptyState icon={Icon} text="No data in this window yet." /> : children}
    </div>
  );
}

function EmptyState({ icon: Icon, text, spinning }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <Icon className={`h-5 w-5 text-slate-400 mb-2 ${spinning ? 'animate-spin' : ''}`} />
      <p className="text-[13px] text-slate-500">{text}</p>
    </div>
  );
}
